// Medir y acomodar texto sin navegador (diseño 4a, §2.2). La tabla de anchos sale de los WOFF versionados (metricas.ts). La pieza
// reserva más de lo que ocupa el texto en el navegador: factor 1.10 sobre la tabla y 2 px por lado. La prueba de colisiones
// (app/scripts/piezas_colisiones.ts) usa sus propias cajas, más chicas (factor 1.05, sin relleno): una pieza que reserve de menos falla.
import { ANCHOS, ASCENDENTE, DESCENDENTE, UNIDADES } from './metricas.ts';
import type { Caja } from './dibujo.ts';

export type Peso = 400 | 500 | 600 | 700;

/** Factor sobre la tabla y relleno por lado (px) que reserva la pieza. */
export const FACTOR = 1.1;
export const RELLENO = 2;
/** Separación entre cajas y margen al borde del lienzo (px). */
export const SEPARACION = 4;
export const BORDE = 8;
/** Lo que se agrega al acortar. */
export const PUNTOS = '…';

const COLUMNA: Record<Peso, number> = { 400: 1, 500: 2, 600: 3, 700: 4 };
const TABLAS = new Map<Peso, Map<number, number>>();
const MAS_ANCHA = new Map<Peso, number>();
for (const p of [400, 500, 600, 700] as Peso[]) {
  TABLAS.set(p, new Map(ANCHOS.map((f) => [f[0], f[COLUMNA[p]] ?? 0])));
  MAS_ANCHA.set(p, Math.max(...ANCHOS.map((f) => f[COLUMNA[p]] ?? 0)));
}

/** Ancho según la tabla, sin factor ni relleno. Una letra que la tabla no tiene mide lo que la más ancha. */
export function anchoTabla(texto: string, tam: number, peso: Peso): number {
  const tabla = TABLAS.get(peso)!;
  const maximo = MAS_ANCHA.get(peso)!;
  let u = 0;
  for (const c of texto.normalize('NFC')) u += tabla.get(c.codePointAt(0)!) ?? maximo;
  return (u * tam) / UNIDADES;
}

export interface Medida {
  /** Ancho reservado. */
  w: number;
  /** Alto reservado (arriba + abajo). */
  h: number;
  /** De la línea base hacia arriba y hacia abajo. */
  arriba: number;
  abajo: number;
}

/** Lo que la pieza reserva para un texto en una línea: factor 1.10 y 2 px por lado. */
export function medir(texto: string, tam: number, peso: Peso): Medida {
  const w = anchoTabla(texto, tam, peso) * FACTOR + 2 * RELLENO;
  const arriba = (ASCENDENTE * tam * FACTOR) / UNIDADES + RELLENO;
  const abajo = (-DESCENDENTE * tam * FACTOR) / UNIDADES + RELLENO;
  return { w, h: arriba + abajo, arriba, abajo };
}

/** La caja reservada de un texto anclado en (x, y), con y en la línea base. */
export function cajaTexto(x: number, y: number, ancla: 'inicio' | 'medio' | 'fin', contenido: string, tam: number, peso: Peso): Caja {
  const m = medir(contenido, tam, peso);
  const x0 = ancla === 'inicio' ? x - RELLENO : ancla === 'fin' ? x - m.w + RELLENO : x - m.w / 2;
  return { x: x0, y: y - m.arriba, w: m.w, h: m.h };
}

/** Régimen societario al final de un nombre (lista cerrada). Se quita sólo si el nombre no cabe. */
const REGIMEN = new RegExp(
  String.raw`(?:,\s*|\s+)(?:` +
    [
      String.raw`S\.?\s?A\.?\s?P\.?\s?I\.?(?:\s+DE\s+C\.?\s?V\.?)?`,
      String.raw`S\.?\s?A\.?\s?B\.?(?:\s+DE\s+C\.?\s?V\.?)?`,
      String.raw`S\.?\s?A\.?(?:\s+DE\s+C\.?\s?V\.?)?`,
      String.raw`S\.?\s?DE\s+R\.?\s?L\.?(?:\s+DE\s+C\.?\s?V\.?)?`,
      String.raw`S\.?\s?C\.?(?:\s+DE\s+R\.?\s?L\.?(?:\s+DE\s+C\.?\s?V\.?)?)?`,
      String.raw`S\.?\s?P\.?\s?R\.?(?:\s+DE\s+R\.?\s?[LI]\.?)?`,
      String.raw`S\.?\s?EN\s+C\.?(?:\s+POR\s+A\.?)?`,
      String.raw`A\.?\s?C\.?`,
      String.raw`I\.?\s?A\.?\s?P\.?`,
      String.raw`SOCIEDAD\s+AN[OÓ]NIMA(?:\s+(?:PROMOTORA\s+DE\s+INVERSI[OÓ]N\s+)?DE\s+CAPITAL\s+VARIABLE)?`,
      String.raw`SOCIEDAD\s+DE\s+RESPONSABILIDAD\s+LIMITADA(?:\s+DE\s+CAPITAL\s+VARIABLE)?`,
      String.raw`SOCIEDAD\s+CIVIL`,
      String.raw`ASOCIACI[OÓ]N\s+CIVIL`,
    ].join('|') +
    String.raw`)\s*$`,
  'i',
);

/** El nombre sin su régimen societario («TRANSPORTES LARA, S.A. DE C.V.» → «TRANSPORTES LARA»). */
export function sinRegimen(nombre: string): string {
  const s = nombre.replace(REGIMEN, '').trim();
  return s.length > 0 ? s : nombre;
}

export interface Acomodo {
  lineas: string[];
  /** Si se quitó el régimen o se cortó con «…». */
  acortado: boolean;
}

const cabe = (t: string, anchoMax: number, tam: number, peso: Peso) => medir(t, tam, peso).w <= anchoMax;

/** Parte una palabra que no cabe sola en una línea, letra por letra (sin guion: cada parte sigue siendo texto del original). */
function partirPalabra(palabra: string, anchoMax: number, tam: number, peso: Peso): string[] {
  const partes: string[] = [];
  let actual = '';
  for (const c of palabra) {
    if (actual && !cabe(actual + c, anchoMax, tam, peso)) {
      partes.push(actual);
      actual = c;
    } else actual += c;
  }
  if (actual) partes.push(actual);
  return partes;
}

/** Corte por palabras, llenando cada línea hasta el límite. */
function partir(texto: string, anchoMax: number, tam: number, peso: Peso): string[] {
  const lineas: string[] = [];
  let actual = '';
  for (const palabra of texto.split(' ').filter(Boolean)) {
    const prueba = actual ? `${actual} ${palabra}` : palabra;
    if (cabe(prueba, anchoMax, tam, peso)) {
      actual = prueba;
      continue;
    }
    if (actual) lineas.push(actual);
    if (cabe(palabra, anchoMax, tam, peso)) actual = palabra;
    else {
      const trozos = partirPalabra(palabra, anchoMax, tam, peso);
      lineas.push(...trozos.slice(0, -1));
      actual = trozos.at(-1) ?? '';
    }
  }
  if (actual) lineas.push(actual);
  return lineas;
}

/**
 * Acomoda un texto en a lo más `lineasMax` líneas de `anchoMax` px (caja reservada). Corta por palabras; si no cabe, quita el régimen
 * societario y, como último recurso, corta la última línea con «…». El texto completo lo guarda quien pinta (campo `completo`).
 */
export function acomodar(texto: string, anchoMax: number, lineasMax: number, tam: number, peso: Peso): Acomodo {
  const limpio = texto.normalize('NFC').replace(/\s+/g, ' ').trim();
  const directo = partir(limpio, anchoMax, tam, peso);
  if (directo.length <= lineasMax) return { lineas: directo, acortado: false };
  const corto = sinRegimen(limpio);
  const base = corto !== limpio ? partir(corto, anchoMax, tam, peso) : directo;
  if (base.length <= lineasMax) return { lineas: base, acortado: true };
  // Último recurso: las primeras líneas enteras y la última con lo que quepa antes de «…».
  const lineas = base.slice(0, lineasMax - 1);
  // Lo que queda, tomado del texto (no de las líneas, que pueden partir una palabra): así cada línea es un trozo del original.
  let cursor = 0;
  for (const l of lineas) cursor = corto.indexOf(l, cursor) + l.length;
  const resto = corto.slice(cursor).trim();
  let ultima = '';
  for (const c of resto) {
    if (!cabe((ultima + c).trimEnd() + PUNTOS, anchoMax, tam, peso)) break;
    ultima += c;
  }
  lineas.push(ultima.trimEnd() + PUNTOS);
  return { lineas, acortado: true };
}
